import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { usePullWorknumber } from "../api/usePullWorknumber";
import { useWorknumberEligibility } from "../api/useWorknumberEligibility";
import { useWorknumberHistory } from "../api/useWorknumberHistory";
import { PurchasedEmployerInfoPanel } from "./PurchasedEmployerInfoPanel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PullPurchasedEmployerInfoProperties {
  /** Deal / approval identifier — path param for all three worknumber calls. */
  approvalId?: string;
  /** Applicant identifier — path param for all three worknumber calls. */
  applicantId?: string;
  /** Stable id for the results table (aria + testing). */
  panelId: string;
}

// ---------------------------------------------------------------------------
// PullPurchasedEmployerInfo
//
// Orchestrates the "Pull Purchased Employer Info" flow for one applicant income
// block:
//   1. /eligibility runs first. `canPull === true` → offer the pull button.
//   2. Button click → POST /worknumber pulls fresh history → render the table.
//   3. `canPull === false` → read /history; render the table if prior data exists.
//
// The whole feature stays inert until both ids are present (approvalId is threaded
// from the page; applicantId is currently stubbed by IncomeBlock — see
// TODO(OR-22604)), so it never fires a doomed request.
// ---------------------------------------------------------------------------
export function PullPurchasedEmployerInfo({ approvalId, applicantId, panelId }: PullPurchasedEmployerInfoProperties) {
  const { t } = useTranslation();
  const hasIds = Boolean(approvalId && applicantId);

  const eligibility = useWorknumberEligibility(approvalId ?? "", applicantId ?? "", { enabled: hasIds });
  const canPull = eligibility.data?.canPull === true;
  const notEligible = eligibility.isSuccess && !canPull;

  // History is the fallback read — only when the applicant can't pull fresh data.
  const history = useWorknumberHistory(approvalId ?? "", applicantId ?? "", { enabled: hasIds && notEligible });

  const pull = usePullWorknumber();

  if (!hasIds) {
    return null;
  }

  if (eligibility.isLoading) {
    return <p className="text-muted-foreground text-xs">{t("review.income.employerHistory.loading")}</p>;
  }

  // Eligibility is a passive probe: if it can't be determined, silently hide the
  // feature rather than surfacing an error. (Errors for the user-initiated pull
  // and the history read below ARE surfaced.)
  if (eligibility.isError) {
    return null;
  }

  // Eligible: offer the pull button, then render whatever it pulls.
  if (canPull) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-start">
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="px-1.5"
            disabled={pull.isPending}
            onClick={() => pull.mutate({ approvalId: approvalId ?? "", applicantId: applicantId ?? "" })}
          >
            {pull.isPending ? t("review.income.employerHistory.pulling") : t("review.income.pullEmployerInfo")}
          </Button>
        </div>
        {/* User-initiated action: surface a failure inline so the click isn't a no-op. */}
        {pull.isError && (
          <p role="alert" className="text-destructive text-xs">
            {t("review.income.employerHistory.error", { message: getErrorMessage(pull.error) })}
          </p>
        )}
        {pull.data && pull.data.rows.length > 0 && (
          <PurchasedEmployerInfoPanel panelId={panelId} rows={pull.data.rows} />
        )}
      </div>
    );
  }

  // Not eligible: surface previously-pulled history if any exists.
  if (history.isLoading) {
    return <p className="text-muted-foreground text-xs">{t("review.income.employerHistory.loading")}</p>;
  }

  if (history.isError) {
    return (
      <p role="alert" className="text-destructive text-xs">
        {t("review.income.employerHistory.error", { message: getErrorMessage(history.error) })}
      </p>
    );
  }

  if (history.data && history.data.rows.length > 0) {
    return <PurchasedEmployerInfoPanel panelId={panelId} rows={history.data.rows} />;
  }

  return null;
}
